/**
 * Utility functions for image optimization
 */

/**
 * Creates a tiny base64-encoded version of an image for use as a placeholder
 * Uses a canvas element to resize the image to ~10px width and returns a base64 data URL
 *
 * @param {string} src - The source URL of the original image
 * @param {number} size - The size of the tiny placeholder (default: 10)
 * @returns {Promise<string>} - A promise that resolves to the base64 data URL
 */
export const generateTinyPlaceholder = (src, size = 10) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No source provided'));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      
      // Calculate proportional height
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const width = size;
      const height = Math.round(size / aspectRatio);
      
      // Set canvas size
      canvas.width = width;
      canvas.height = height;
      
      // Draw image at reduced size
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Get data URL (base64)
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.3); // Low quality JPEG
        resolve(dataUrl);
      } catch (e) {
        // Handle cross-origin issues
        reject(new Error('Could not generate placeholder: CORS issue'));
      }
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${src}`));
    };
    
    img.src = src;
  });
};

/**
 * Preloads an image by creating a hidden Image object
 * 
 * @param {string} src - The source URL of the image to preload
 * @returns {Promise<HTMLImageElement>} - A promise that resolves to the loaded image
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No source provided'));
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to preload image: ${src}`));
    img.src = src;
  });
};

/**
 * Checks if current browser supports modern image formats like WebP or AVIF
 * 
 * @returns {Promise<Object>} - Object with support info for various formats
 */
export const checkImageFormatSupport = async () => {
  const supports = {
    webp: false,
    avif: false,
    jpeg2000: false
  };
  
  // Helper function to check format support
  const checkSupport = (format) => {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = function() {
        resolve(this.width > 0 && this.height > 0);
      };
      
      img.onerror = function() {
        resolve(false);
      };
      
      img.src = `data:image/${format};base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=`;
    });
  };
  
  try {
    supports.webp = await checkSupport('webp');
    supports.avif = await checkSupport('avif');
    supports.jpeg2000 = await checkSupport('jp2');
  } catch (e) {
    console.warn('Error checking image format support:', e);
  }
  
  return supports;
};

/**
 * Gets appropriate image URL based on device capabilities
 * If the browser supports modern formats, it will return the optimized URL
 * 
 * @param {string} originalSrc - Original image source
 * @param {number} width - Desired width
 * @param {Object} formatSupport - Result from checkImageFormatSupport
 * @returns {string} - The most appropriate image URL
 */
export const getOptimizedImageUrl = (originalSrc, width = 800, formatSupport = null) => {
  // If no format support object provided or no src, return original
  if (!originalSrc || !formatSupport) return originalSrc;
  
  // Only process URLs from our own domain
  if (!originalSrc.includes('dance.digital')) return originalSrc;
  
  // Parse the URL
  try {
    const url = new URL(originalSrc);
    const params = new URLSearchParams(url.search);
    
    // Add width parameter
    params.set('w', width.toString());
    
    // Add format parameter based on browser support
    if (formatSupport.avif) {
      params.set('fmt', 'avif');
    } else if (formatSupport.webp) {
      params.set('fmt', 'webp');
    }
    
    // Set quality (slightly lower for better performance)
    params.set('q', '85');
    
    // Update URL search parameters
    url.search = params.toString();
    return url.toString();
  } catch (e) {
    console.warn('Error creating optimized URL:', e);
    return originalSrc;
  }
}; 